import { useQuery } from '@tanstack/react-query';
import type { PublicUser } from '@avslack/shared';
import { getMessageRevisions } from '../lib/api';
import { formatDay, formatTime } from '../lib/format';
import { RichText } from './RichText';
import { Icon } from './Icon';

/**
 * A "(szerkesztve)" jelzes mogotti elozmeny.
 *
 * ─── Miert kell? ────────────────────────────────────────────────────────────
 *
 * Mert a jelzes magaban csak annyit mond, hogy valami megvaltozott - azt nem,
 * hogy MI. Aki kesobb olvassa a beszelgetest, igy nem tudja eldonteni, hogy
 * amire valaszoltak, ugyanaz volt-e, mint amit most lat. Egy csapatban ez
 * rosszabb, mint amilyennek latszik.
 *
 * ─── Ki lathatja? ───────────────────────────────────────────────────────────
 *
 * Aki magat az uzenetet olvashatja. Nem a szerzo kivaltsaga: ott lehetett,
 * amikor az meg az volt. Kulon jogosultsagot adni ide azt jelentene, hogy a
 * szerkesztessel utolag el lehet rejteni valamit azok elol, akik mar olvastak.
 */
export function MessageHistory({
  messageId,
  meId,
  usersById,
  onClose,
}: {
  messageId: string;
  meId: string;
  usersById: Map<string, PublicUser>;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['message-revisions', messageId],
    queryFn: () => getMessageRevisions(messageId),
    /*
      Nincs hosszu ervenyesseg: a panelt kezzel nyitjak ki, ritkan, es akkor a
      FRISS allapot kell. Egy percig eltett lista epp az imenti szerkesztest
      hagyna ki belole.
    */
    staleTime: 0,
  });

  return (
    <div className="mt-1.5 rounded-card border border-border-soft bg-surface-sunken p-2.5">
      <div className="mb-1.5 flex items-center gap-2">
        <p className="min-w-0 flex-1 text-2xs font-medium uppercase tracking-wide text-ink-faint">
          Korábbi változatok
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Bezárás"
          className="text-ink-faint transition-colors hover:text-ink"
        >
          <Icon name="bezaras" size="sm" />
        </button>
      </div>

      {isLoading && <p className="text-2xs text-ink-faint">Betöltés…</p>}

      {/* A hiba NEM ugyanaz, mint az ures elozmeny - kulonben "nincs mit mutatni"-nak latszana. */}
      {isError && (
        <p role="alert" className="text-2xs text-danger">
          Nem sikerült betölteni a korábbi változatokat. Zárd be, és nyisd ki újra.
        </p>
      )}

      {/*
        Az URES lista NEM azt jelenti, hogy nem volt szerkesztes.

        A valtozatok megorzese egy adott naptol mukodik; az azelott
        szerkesztett uzeneteknel nincs mit mutatni. Ezt kimondjuk - egy ures
        doboz azt sugallna, hogy a szerkesztes sem tortent meg.
      */}
      {data?.length === 0 && (
        <p className="text-2xs text-ink-faint">
          Ez az üzenet még azelőtt lett szerkesztve, hogy a korábbi változatokat megőriztük volna.
        </p>
      )}

      {/* Sajat cimke all minden sor elen ("Eredeti", "2. valtozat") - a lista
          sajat szamozasa melettuk csak ismetles lenne. */}
      <ol className="list-none space-y-2">
        {(data ?? []).map((revision, index) => (
          <li key={revision.id}>
            <p className="mb-0.5 text-2xs text-ink-faint">
              {index === 0 ? 'Eredeti' : `${index + 1}. változat`} · átírva{' '}
              {formatDay(revision.replacedAt)} {formatTime(revision.replacedAt)}
            </p>
            <div className="avslack-prose break-words text-sm text-ink-muted">
              {revision.body ? (
                <RichText doc={revision.body} meId={meId} usersById={usersById} />
              ) : (
                <p className="whitespace-pre-wrap">{revision.text}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
